import React, { useCallback, useMemo } from "react"
import { Button, Flex, Text } from "@chakra-ui/react"
import { Product, ProductVariant } from "@shopify/hydrogen-react/storefront-api-types"

import { useCart } from "@app/hooks/useCart"
import { useCore } from "@app/hooks/useCore"
import { useShopifyPrice } from "@app/hooks/useShopify"
import { useOutOfStock } from "@app/hooks/useOutOfStock"
import "@app/theme/css/product.css"
import { useGlobalContext } from "@app/providers/global"

type Props = {
  handleBackInStock: () => void
  handleFindInStore?: () => void
  isUnavailable: boolean
  loading?: boolean
  product?: Product
  quantity: number
  variant: ProductVariant | null
}

export const ProductStockAddToCart: React.FC<Props> = React.memo(
  ({ handleBackInStock, handleFindInStore, product, quantity, loading, isUnavailable, variant }) => {
    const { addToCart, loading: cartLoading } = useCart()
    const { additionalAddToCartProduct, additionalAddToCartLoading, additionalInStoreOnly } = useGlobalContext()
    const activeVariant = variant
    const { formattedPrice, formattedCompareAtPrice, onSale } = useShopifyPrice(activeVariant, quantity)
    const {
      helpers: { decodeShopifyId },
    } = useCore()

    const shopifyVariant = useMemo(
      () => product?.variants.find(v => decodeShopifyId(v.id, "ProductVariant") === decodeShopifyId(activeVariant?.id, "ProductVariant")),
      [product?.variants, activeVariant?.id, decodeShopifyId]
    )

    const available = useMemo(
      () => product?.published && shopifyVariant?.availableForSale && isUnavailable === false,
      [shopifyVariant?.availableForSale, product?.published, isUnavailable]
    )

    const { stockStatus, isNotifyFlow, isDisabledFlow } = useOutOfStock(product)

    const text = useMemo(() => {
      if (product?.inStoreOnly) {
        return additionalInStoreOnly
      } else {
        return cartLoading ? additionalAddToCartLoading : additionalAddToCartProduct
      }
    }, [additionalAddToCartLoading, additionalAddToCartProduct, additionalInStoreOnly, cartLoading, product?.inStoreOnly])

    const onClick = useCallback(async () => {
      if (available && activeVariant && !loading && !cartLoading) {
        await addToCart(product, activeVariant, quantity)
        if (handleFindInStore) handleFindInStore()
      }
    }, [available, activeVariant, loading, cartLoading, quantity, product, addToCart, handleFindInStore])

    return (
      <Button
        variant={isNotifyFlow || product?.inStoreOnly || isDisabledFlow ? "fill" : "fillRainbowLoader"}
        justifyContent="center"
        w="100%"
        maxW="none"
        alignItems="center"
        isDisabled={isDisabledFlow || cartLoading || product?.inStoreOnly}
        onClick={available ? onClick : handleBackInStock}
        mt={2}
        transition="none"
        aria-label={stockStatus || text}
        title={stockStatus || text}
      >
        {stockStatus ? (
          stockStatus
        ) : (
          <>
            <Text variant="text18">{stockStatus || text}</Text>
            <Flex ml="10px" display="inline-flex">
              {onSale && (
                <Text variant="text18" pr="5px" textDecoration="line-through">
                  {formattedCompareAtPrice}
                </Text>
              )}
              <Text variant="text18">{formattedPrice}</Text>
            </Flex>
          </>
        )}
      </Button>
    )
  }
)
